import { DateTime } from "luxon";

/** One of Management Center's three shift-time bands, in Day → Evening → Night order. */
export type ShiftPeriod = "Day" | "Evening" | "Night";

/** Canonical ordering, for anywhere the three periods render as a sequence (legend, filter list). */
export const SHIFT_PERIOD_ORDER: readonly ShiftPeriod[] = ["Day", "Evening", "Night"];

// The hour bands of Management Center's old `getShiftCategory`: Day [6,14), Evening [14,22), Night
// otherwise (22:00–05:59). What changed is which spellings get a band. The old rule read any leading
// number (`parseInt`), so "7am" was Day; now only `SHIFT_CLOCK_TIME_PATTERN` times are read, and
// Management Center shows anything else, "7am" included, as Night.
export const DAY_START_HOUR = 6;
export const EVENING_START_HOUR = 14;
export const NIGHT_START_HOUR = 22;

/**
 * The one clock grammar for `shift_template` times: 24-hour H:MM or H:MM:SS, the hour with one or two
 * digits (0–23), so "7:00", "07:00", "7:00:00" and "07:00:00" are all 7:00. Nothing else: no "7am", no
 * "24:00", no padding. The backend's `CLOCK_TIME_PATTERN` (`backend/util/clock_time.py`) is the
 * same grammar, and both are held to the `clockCases` in `shared/test-vectors/location_duplicates.json`;
 * change them together.
 */
export const SHIFT_CLOCK_TIME_PATTERN = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/** True for a clock time in `SHIFT_CLOCK_TIME_PATTERN`, which `shiftPeriodOf` and `shiftClockSeconds` read without throwing. */
export function isShiftClockTime(value: string): boolean {
	return SHIFT_CLOCK_TIME_PATTERN.test(value);
}

/** Seconds past midnight of a `SHIFT_CLOCK_TIME_PATTERN` time ("7:00", "7:00:00" and "07:00:00" are all 25200). Throws on anything else. */
export function shiftClockSeconds(value: string): number {
	if (!isShiftClockTime(value)) {
		throw new Error(`"${value}" is not a 24-hour H:MM or H:MM:SS clock time`);
	}
	const [hours, minutes, seconds] = value.split(":").map(Number);
	// DEFAULT: 0 seconds. An H:MM time has no `:SS` part, and that IS zero seconds past the minute; nothing is missing.
	const secondsPart = seconds === undefined ? 0 : seconds;
	return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + secondsPart;
}

/**
 * Seconds past midnight of a `SHIFT_CLOCK_TIME_PATTERN` time, or null for a stored time outside the grammar. For the
 * callers that must still handle such a row (sort it, key it, skip it, refuse it); each says what null means to it.
 */
export function readableClockSeconds(value: string): number | null {
	return isShiftClockTime(value) ? shiftClockSeconds(value) : null;
}

/**
 * Two stored times compared as clock values ("7:00" == "07:00:00") when both are readable; otherwise as text, since
 * neither can be compared as a time. The rule the backend's recurring-shift cascade and duplicate rule (b) use.
 */
export function sameClockTime(a: string, b: string): boolean {
	const aSeconds = readableClockSeconds(a);
	const bSeconds = readableClockSeconds(b);
	return aSeconds !== null && bSeconds !== null ? aSeconds === bSeconds : a === b;
}

/** The Luxon format of a shift time's 12-hour label: "7:00 AM". */
export const SHIFT_CLOCK_LABEL_FORMAT = "h:mm a";

/** The Luxon format a shift form holds its times in: 24-hour "07:00". */
export const SHIFT_CLOCK_FORM_FORMAT = "HH:mm";

/**
 * A `SHIFT_CLOCK_TIME_PATTERN` time as a wall-clock `DateTime` to the minute (seconds dropped, as a shift window is
 * read to the minute), for a caller that formats it. Throws on anything else. The date is meaningless; the zone only
 * has to be fixed, so the time can't shift with the browser's.
 */
export function shiftClockDateTime(value: string): DateTime {
	const seconds = shiftClockSeconds(value);
	return DateTime.fromObject(
		{ hour: Math.floor(seconds / SECONDS_PER_HOUR), minute: Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE) },
		{ zone: "utc" },
	);
}

/** A `SHIFT_CLOCK_TIME_PATTERN` time in a Luxon `format` (e.g. `SHIFT_CLOCK_FORM_FORMAT`: "7:00:00" → "07:00"). Throws on anything else. */
export function formatShiftClockTimeAs(value: string, format: string): string {
	return shiftClockDateTime(value).toFormat(format);
}

/**
 * A `SHIFT_CLOCK_TIME_PATTERN` time on the 12-hour clock: "7:00", "07:00", "7:00:00" and "07:00:00" are all
 * "7:00 AM". Throws on anything else. Seconds are dropped from the label, as a shift window is read to
 * the minute.
 */
export function formatShiftClockTime(value: string): string {
	return formatShiftClockTimeAs(value, SHIFT_CLOCK_LABEL_FORMAT);
}

/**
 * Which of the three Management Center shift periods a start time falls in. Reads any
 * `SHIFT_CLOCK_TIME_PATTERN` time: "7:00", "07:00", "7:00:00" and "07:00:00" (some `shift_template` rows store seconds).
 *
 * Throws on an unparsable time — fail loud, per the project's no-silent-defaults rule. Management
 * Center's alerts grid and shift-hours filter render live `shift_template` data and must not crash
 * on a bad row, so those call sites validate with `isShiftClockTime` first rather than calling
 * this blind; see the `// DEFAULT:` comment at each one for why "Night" is what they fall back to.
 */
export function shiftPeriodOf(startTime: string): ShiftPeriod {
	if (!isShiftClockTime(startTime)) {
		throw new Error(`shiftPeriodOf: unparsable start time "${startTime}"`);
	}
	const hour = Math.floor(shiftClockSeconds(startTime) / SECONDS_PER_HOUR);
	if (hour >= DAY_START_HOUR && hour < EVENING_START_HOUR) return "Day";
	if (hour >= EVENING_START_HOUR && hour < NIGHT_START_HOUR) return "Evening";
	return "Night";
}
